import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen, Users, Target,
  Calendar, Download, FileDown, Filter, Search,
  XCircle,
  ArrowUp, ArrowDown, Activity, Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { coursePerformanceService } from '@/services/course-performance.service';
import { exportService } from '@/services/export.service';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { categoryService } from '@/services/category.service';
import { userService } from '@/services/user.service';
import type { CoursePerformanceItemDto, CoursePerformanceListParams, Granularity } from '@/types/course-performance.types';
import type { CategoryDropdownItem } from '@/types/category.types';
import toast from 'react-hot-toast';
import Pagination from '@/components/ui/Pagination';
import { MetricCardSkeleton, TableSkeleton } from '@/components/ui/SkeletonLoader';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { COURSE_PERFORMANCE_PERMISSION } from '@/constants/constant-permissions';


interface DepartmentOption {
  group_name: string;
  group_key: string;
  name?: string;
  roles?: { role_key: string; role_name: string }[];
}


interface CourseDetails extends CoursePerformanceItemDto {
  category?: string;
  instructor?: string;
  duration?: string;
  level?: 'Beginner' | 'Intermediate' | 'Advanced';
  createdDate?: string;
  lastUpdated?: string;
  thumbnail?: string;
  price?: number;
  modules?: {
    name: string;
    completionRate: number;
    averageTime: number;
    difficulty: 'easy' | 'medium' | 'hard';
  }[];
  engagement?: {
    videoWatchTime: number;
    assignmentsCompleted: number;
    discussionPosts: number;
    resourceDownloads: number;
  };
  departments?: {
    name: string;
    enrolled: number;
    completed: number;
  }[];
}

interface CourseMetricCard {
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  subtitle: string;
}

interface FiltersState {
  timeRange: Granularity | 'all' | 'custom';
  category: string;
  status: CoursePerformanceListParams['status'] | 'all';
  searchQuery: string;
  department: string;
  role: string;
  page: number;
  limit: number;
}


export default function CoursePerformance() {
  usePermissionRouteAccess([COURSE_PERFORMANCE_PERMISSION.ADMIN_COURSE_PERFORMANCE_READ, COURSE_PERFORMANCE_PERMISSION.ADMIN_COURSE_PERFORMANCE_WRITE]);
  const canWrite = useHasPermission([COURSE_PERFORMANCE_PERMISSION.ADMIN_COURSE_PERFORMANCE_WRITE]);

  const [categoryList, setCategoryList] = useState<CategoryDropdownItem[]>([]);
  const [exportLoading, setExportLoading] = useState<{ excel: boolean; csv: boolean }>({ excel: false, csv: false });
  // --- ADDED: State for toggling extra filters ---
  const [showFilters, setShowFilters] = useState(false);
  // --- ADDED: State for custom date range ---
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');


  const [filters, setFilters] = useState<FiltersState>({
    timeRange: 'all',
    category: 'all',
    status: 'all',
    searchQuery: '',
    department: 'all',
    role: '',
    page: 1,
    limit: 10
  });

  const defaultCategories: CategoryDropdownItem[] = [
    { _id: 'technology', category_name: 'Technology' },
    { _id: 'sales', category_name: 'Sales' },
    { _id: 'leadership', category_name: 'Leadership' },
    { _id: 'compliance', category_name: 'Compliance' },
    { _id: 'marketing', category_name: 'Marketing' },
    { _id: 'finance', category_name: 'Finance' },
    { _id: 'hr', category_name: 'Human Resources' },
  ];

  const { data: categoriesData } = useQuery({
    queryKey: ['categoryDropdown'],
    queryFn: async () => {
      try {
        const data = await categoryService.getCategoryDropdown();
        return data || defaultCategories;
      } catch (error) {
        console.error('Failed to fetch categories, using defaults:', error);
        return defaultCategories;
      }
    },
    staleTime: 5 * 60 * 1000,
  });


  const { data: departmentsData } = useQuery({
    queryKey: ['departments-dropdown'],
    queryFn: () => userService.getGroupsDropdown(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setCategoryList(categoriesData || defaultCategories);
  }, [categoriesData]);

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExportLoading(prev => ({ ...prev, [format]: true }));

      const selectedDept = departmentsData?.data?.find((d: DepartmentOption) =>
        (d.group_name || d.name) === filters.department
      );

      const exportFilters = {
        timeRange: filters.timeRange,
        status: filters.status !== 'all' ? filters.status : undefined,
        search: filters.searchQuery || undefined,
        sortBy: undefined as string | undefined,
        sortOrder: undefined as string | undefined,
        start_date: filters.timeRange === 'custom' ? startDate : undefined,
        end_date: filters.timeRange === 'custom' ? endDate : undefined,
        groupKey: selectedDept?.group_key || undefined,
        roleKey: filters.role || undefined,
        categoryId: filters.category !== 'all' ? filters.category : undefined,
        categoryName: filters.category !== 'all' ? categoryList.find(c => c._id === filters.category)?.category_name : undefined,
      };

      const result = await exportService.exportCoursePerformance(format, exportFilters);

      if (result.success) {
        toast.success(`Exported ${result.count} courses to ${format.toUpperCase()}`);
      } else {
        toast.error(result.message || 'No data to export');
      }
    } catch (error: any) {
      toast.error('Failed to export course performance data');
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ['coursePerformanceMetrics', filters.timeRange, startDate, endDate],
    queryFn: () => {
      if (filters.timeRange === 'custom' && startDate && endDate) {
        return coursePerformanceService.getMetrics({
          start_date: startDate,
          end_date: endDate
        });
      }
      return coursePerformanceService.getMetrics(
        filters.timeRange !== 'all' && filters.timeRange !== 'custom' ? { granularity: filters.timeRange } : undefined
      );
    },
    enabled: filters.timeRange !== 'custom' || (filters.timeRange === 'custom' && !!startDate && !!endDate),
  });


const buildApiFilters = (): CoursePerformanceListParams => {
    const apiFilters: CoursePerformanceListParams = {
      page: filters.page,
      limit: filters.limit,
      roleKey: '',
      groupKey: '',
      categoryId: ''
    };

    if (filters.searchQuery) apiFilters.search = filters.searchQuery;
    if (filters.status !== 'all') apiFilters.status = filters.status;

    // Handle custom date range or granularity
    if (filters.timeRange === 'custom' && startDate && endDate) {
      apiFilters.start_date = startDate;
      apiFilters.end_date = endDate;
    } else if (filters.timeRange !== 'all' && filters.timeRange !== 'custom') {
      apiFilters.granularity = filters.timeRange;
    }

    if (filters.category !== 'all') apiFilters.categoryId = filters.category;

    if (filters.department !== 'all') {
        const selectedDept = departmentsData?.data?.find((dept: DepartmentOption) =>
            (dept.group_name || dept.name) === filters.department
        );
        if (selectedDept?.group_key) {
            apiFilters.groupKey = selectedDept.group_key;
        }
    }

    if (filters.role) {
        apiFilters.roleKey = filters.role;
    }

    return apiFilters;
};

  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['coursePerformanceList', filters, startDate, endDate],
    queryFn: () => coursePerformanceService.getCoursePerformanceList(buildApiFilters()),
    enabled: filters.timeRange !== 'custom' || (filters.timeRange === 'custom' && !!startDate && !!endDate),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.searchQuery !== '') {
          setFilters(prev => ({ ...prev, page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.searchQuery]);


  const mapCourseToDetails = (course: CoursePerformanceItemDto): CourseDetails => {
    const categoryName = categoryList.find(cat => cat._id === course.course_id)?.category_name || 'General';

    return {
      ...course,
      category: categoryName,
      instructor: 'Instructor Name',
      duration: '8 weeks',
      level: 'Intermediate' as const,
      createdDate: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      thumbnail: course.thumbnail_image_url || 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400',
      price: 0,
      modules: [
        { name: 'Module 1', completionRate: 85, averageTime: 3.5, difficulty: 'easy' },
        { name: 'Module 2', completionRate: 72, averageTime: 4.2, difficulty: 'medium' },
        { name: 'Module 3', completionRate: 58, averageTime: 5.8, difficulty: 'hard' },
      ],
      engagement: {
        videoWatchTime: Math.round(course.total_user_enrollments * 45),
        assignmentsCompleted: Math.round(course.total_user_enrollments * 3.5),
        discussionPosts: Math.round(course.total_user_enrollments * 0.8),
        resourceDownloads: Math.round(course.total_user_enrollments * 2.1),
      },
      departments: [
        { name: 'Engineering', enrolled: Math.round(course.total_user_enrollments * 0.4), completed: Math.round(course.total_user_enrollments * 0.3) },
        { name: 'Sales', enrolled: Math.round(course.total_user_enrollments * 0.3), completed: Math.round(course.total_user_enrollments * 0.2) },
        { name: 'Marketing', enrolled: Math.round(course.total_user_enrollments * 0.3), completed: Math.round(course.total_user_enrollments * 0.1) },
      ],
    };
  };

  const courses = coursesData?.courses.map(mapCourseToDetails) || [];

  // Helper function to get dynamic subtitle based on time range
  const getTimeRangeSubtitle = (): string => {
    if (filters.timeRange === 'custom' && startDate && endDate) {
      return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
    }
    switch (filters.timeRange) {
      case 'weekly': return 'This week';
      case 'monthly': return 'This month';
      case 'quarterly': return 'This quarter';
      case 'yearly': return 'This year';
      case 'all': return 'All time';
      default: return 'All time';
    }
  };

  const overallMetrics: CourseMetricCard[] = [
   {
    title: 'Total Published Courses',
    value: metricsData?.total_active_courses || 0,
    change: metricsData?.changes?.total_active_courses?.label || '-',
    trend: metricsData?.changes?.total_active_courses?.trend || 'neutral',
    icon: <BookOpen className="w-5 h-5" />,
    subtitle: getTimeRangeSubtitle(),
  },
  {
    title: 'Total Enrollments',
    value: metricsData?.total_enrollments?.toLocaleString() || '0',
    change: metricsData?.changes?.total_enrollments?.label || '-',
    trend: metricsData?.changes?.total_enrollments?.trend || 'neutral',
    icon: <Users className="w-5 h-5" />,
    subtitle: getTimeRangeSubtitle(),
  },
  {
    title: 'Avg Completion Rate',
    value: `${metricsData?.avg_completion_rate?.toFixed(1) || 0}%`,
    change: metricsData?.changes?.avg_completion_rate?.label || '-',
    trend: metricsData?.changes?.avg_completion_rate?.trend || 'neutral',
    icon: <Target className="w-5 h-5" />,
    subtitle: getTimeRangeSubtitle(),
  },
  {
    title: 'Active Learners',
    value: metricsData?.active_learners || 0,
    change: metricsData?.changes?.active_learners?.label || '-',
    trend: metricsData?.changes?.active_learners?.trend || 'neutral',
    icon: <Activity className="w-5 h-5" />,
    subtitle: getTimeRangeSubtitle(),
  },
];

  const handleItemsPerPageChange = (newSize: number) => {
    setFilters(prev => ({...prev, limit: newSize, page: 1}));
  };

  const handlePageChange = (newPage: number) => {
      setFilters(prev => ({...prev, page: newPage}));
  };

  const handleTimeRangeChange = (range: Granularity | 'all' | 'custom') => {
    setFilters(prev => ({ ...prev, timeRange: range, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
        timeRange: 'all',
        category: 'all',
        status: 'all',
        searchQuery: '',
        department: 'all',
        role: '',
        page: 1,
        limit: 10,
    });
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Course Performance Analytics</h1>
            <p className="text-brand-indigo-100">
              Analyze course effectiveness, engagement metrics, and identify areas for improvement
              {filters.timeRange === 'custom' && startDate && endDate ? (
                <span className="ml-2 text-white/90">
                  • Showing data from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                </span>
              ) : filters.timeRange !== 'all' ? (
                <span className="ml-2 text-white/90">
                  • Showing data from the last {filters.timeRange}
                </span>
              ) : null}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => canWrite && handleExport('excel')}
              disabled={!canWrite || exportLoading.excel}
              className={`flex items-center gap-2 px-4 py-2 bg-white text-brand-indigo-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${canWrite ? 'hover:bg-brand-indigo-50' : ''}`}
              title={!canWrite ? 'You do not have permission to export' : 'Export as Excel'}
            >
              {exportLoading.excel ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <FileDown className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">
                {exportLoading.excel ? 'Exporting...' : 'Excel'}
              </span>
            </button>
            <button
              onClick={() => canWrite && handleExport('csv')}
              disabled={!canWrite || exportLoading.csv}
              className={`flex items-center gap-2 px-4 py-2 bg-transparent text-white border border-white/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${canWrite ? 'hover:bg-white/10' : ''}`}
              title={!canWrite ? 'You do not have permission to export' : 'Export as CSV'}
            >
              {exportLoading.csv ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">
                {exportLoading.csv ? 'Exporting...' : 'CSV'}
              </span>
            </button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="mt-6 space-y-3">
          {/* Time Range Buttons Row */}
          <div className="flex flex-wrap gap-2">
            {([
              { value: 'all', label: 'All Time' },
              { value: 'weekly', label: 'Week' },
              { value: 'monthly', label: 'Month' },
              { value: 'quarterly', label: 'Quarter' },
              { value: 'yearly', label: 'Year' },
              { value: 'custom', label: 'Custom' },
            ] as const).map((range) => (
              <button
                key={range.value}
                onClick={() => handleTimeRangeChange(range.value)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-all duration-300 transform hover:scale-105 ${
                  filters.timeRange === range.value
                    ? 'bg-white text-brand-indigo-600 shadow-xl scale-105'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                {range.value === 'custom' && <Calendar className="w-4 h-4 inline mr-1" />}
                {range.label}
              </button>
            ))}
          </div>

          {/* Custom Date Range - Inline Expansion */}
          {filters.timeRange === 'custom' && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-white">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || undefined}
                  className="px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border-0 focus:ring-2 focus:ring-white/50 outline-none shadow-lg"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-white">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  className="px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border-0 focus:ring-2 focus:ring-white/50 outline-none shadow-lg"
                />
              </div>
              {startDate && endDate && (
                <span className="text-sm font-medium text-white drop-shadow px-3 py-2 bg-white/20 rounded-lg">
                  {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                </span>
              )}
            </div>
          )}
        </div>
      </div>

       {/* Overall Metrics */}
      {metricsLoading ? (
        <MetricCardSkeleton count={4} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {overallMetrics.map((metric, index) => (
            <div key={index} className="card card-hover">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-primary-100 text-primary-700">
                  {metric.icon}
                </div>
                <span className={`flex items-center gap-1 text-xs font-medium ${
                  metric.trend === 'up' ? 'text-green-600' :
                  metric.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {metric.trend === 'up' ? <ArrowUp className="w-3 h-3" /> :
                   metric.trend === 'down' ? <ArrowDown className="w-3 h-3" /> : null}
                  {metric.change}
                </span>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                <p className="text-sm text-gray-500 mt-1">{metric.title}</p>
                <p className="text-xs text-gray-400 mt-1">{metric.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      )}


      {/* --- MODIFIED: FILTERS SECTION --- */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-4 gap-2">
          {/* Left: Title */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-brand-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filter & Search Courses</h2>
          </div>

          {/* Right: Clear All + Show/Hide Filters */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700 hover:bg-gray-100 transition"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilters(prev => !prev)}
              className="flex items-center gap-1 px-3 py-1 text-sm text-brand-indigo-600 bg-brand-indigo-50 rounded-lg hover:bg-brand-indigo-100 transition"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* --- MODIFIED: Layout is now a 2-column grid for more space --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Search Course Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Course Name"
                  value={filters.searchQuery}
                  onChange={(e) => setFilters(prev => ({...prev, searchQuery: e.target.value}))}
                  className="w-full pl-10 pr-4 h-[42px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-indigo-500 bg-gray-50 hover:bg-white transition-colors"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className='mt-2'>
              <SimpleSelect
                label="Category"
                options={[
                  { label: 'All Categories', value: 'all' },
                  ...categoryList.map(cat => ({ label: cat.category_name, value: cat._id }))
                ]}
                value={filters.category}
                onChange={(val) => setFilters(prev => ({ ...prev, category: (val as string) || 'all', page: 1 }))}
                placeholder="All Categories"
              />
            </div>
          </div>

          {/* --- ADDED: Conditionally rendered filters --- */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
               {/* Status Filter */}
               <div>
                <SimpleSelect
                  label="Status"
                  options={[
                    { label: 'All Status', value: 'all' },
                    { label: 'Published', value: 'published' },
                    { label: 'Unpublished', value: 'unpublished' },
                    { label: 'Draft', value: 'draft' },
                  ]}
                  value={filters.status}
                  onChange={(val) => setFilters(prev => ({ ...prev, status: ((val as string) || 'all') as FiltersState['status'], page: 1 }))}
                  placeholder="All Status"
                />
              </div>

              {/* Department Filter */}
              <div>
                <SimpleSelect
                  label="Department"
                  options={[
                    { label: 'All Departments', value: 'all' },
                    ...(departmentsData?.data?.map((d: DepartmentOption) => ({
                      label: d.group_name || d.name || 'Unknown',
                      value: d.group_name || d.name || 'Unknown'
                    })) || [])
                  ]}
                  value={filters.department}
                  onChange={(val) => setFilters(prev => ({ ...prev, department: (val as string) || 'all', role: '', page: 1 }))}
                  placeholder="All Departments"
                  isSearchable
                />
              </div>

              {/* Role Filter */}
              <div>
                <SimpleSelect
                  label="Role"
                  options={[
                    { label: 'All Roles', value: '' },
                    ...(filters.department && filters.department !== 'all'
                      ? (departmentsData?.data
                          ?.find((d: DepartmentOption) => (d.group_name || d.name) === filters.department)
                          ?.roles?.map((r: { role_key: string; role_name: string }) => ({
                            label: r.role_name || r.role_key,
                            value: r.role_key
                          })) || [])
                      : [])
                  ]}
                  value={filters.role || ''}
                  onChange={(val) => setFilters(prev => ({ ...prev, role: (val as string) || '', page: 1 }))}
                  placeholder="Select Role"
                  isDisabled={filters.department === 'all' || !filters.department}
                  isSearchable
                />
              </div>
            </div>
          )}

          {/* Active Filters Summary */}
          {(filters.searchQuery || filters.category !== 'all' || filters.status !== 'all' || filters.department !== 'all' || filters.role) && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Active filters:</span>
                {filters.searchQuery && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                    Search: "{filters.searchQuery}"
                    <button onClick={() => setFilters(prev => ({...prev, searchQuery: ''}))}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                 {filters.category !== 'all' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100 text-sky-700 text-xs rounded-full">
                    Category: {categoryList.find(c => c._id === filters.category)?.category_name}
                    <button onClick={() => setFilters(prev => ({...prev, category: 'all'}))}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.status !== 'all' && (
                   <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full capitalize">
                    Status: {filters.status}
                    <button onClick={() => setFilters(prev => ({...prev, status: 'all'}))}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.department !== 'all' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    Dept: {filters.department}
                    <button onClick={() => setFilters(prev => ({...prev, department: 'all', role: '', page: 1}))}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.role && (
                   <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    Role: {
                        departmentsData?.data
                        ?.find((d: DepartmentOption) => (d.group_name || d.name) === filters.department)
                        ?.roles?.find((r: { role_key: string; role_name: string }) => r.role_key === filters.role)?.role_name || filters.role
                    }
                    <button onClick={() => setFilters(prev => ({...prev, role: '', page: 1}))}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Course List Table */}
      <div className="card overflow-hidden">
        {coursesLoading ? (
          <TableSkeleton rows={5} columns={5} />
        ) : courses.length === 0 ? (
             <div className="text-center p-12">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No courses found matching the filters.</p>
            </div>
        ) : (
        <>
            <div className="overflow-x-auto">
            <table className="w-full">
                  <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Enrollments</th>
                 <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completed</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">In Progress</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Not Started</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completion</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
                <tbody className="divide-y divide-gray-200">
                {courses.map((course) => (
                    <tr key={course.course_id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                        <img
                            src={course.thumbnail || course.thumbnail_image_url || 'https://via.placeholder.com/48'}
                            alt={course.course_name}
                            className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                            <p className="font-medium text-gray-900">{course.course_name}</p>
                             {/* <p className="text-xs font-bold text-gray-500 mt-1"> ID: {course.course_id}
                            </p> */}
                        </div>
                        </div>
                    </td>
                    <td className="px-4 py-4">
                        <p className="text-sm text-gray-700">{course.category_name || 'N/A'}</p>
                    </td>
                    <td className="px-4 py-4 text-center">
                        <div>
                        <p className="text-sm font-medium text-gray-900">{course.total_user_enrollments}</p>
                        </div>
                    </td>
                     <td className="px-4 py-4 text-center">
                        <p className="text-sm font-medium text-gray-900">{course.completed_user_count}</p>
                    </td>
                    <td className="px-4 py-4 text-center">
                        <p className="text-sm font-medium text-gray-900">{course.in_progress_user_count || 0}</p>
                    </td>
                    <td className="px-4 py-4 text-center">
                        <p className="text-sm font-medium text-gray-900">{course.not_started_user_count || 0}</p>
                    </td>
                    <td className="px-4 py-4">
                        <div className="flex flex-col items-center gap-1">
                        <span className="text-sm font-medium text-gray-900">
                            {course.completion_percentage.toFixed(1)}%
                        </span>
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                            className={`h-2 rounded-full ${
                                course.completion_percentage >= 80 ? 'bg-green-500' :
                                course.completion_percentage >= 60 ? 'bg-yellow-500' :
                                'bg-red-500'
                            }`}
                            style={{ width: `${course.completion_percentage}%` }}
                            />
                        </div>
                        </div>
                    </td>
                    <td className="px-4 py-4">
                        <div className="flex items-center justify-end">
                        <Link
                            to={`/admin/course-performance/${course.course_id}/users`}
                             state={{ from: location.pathname }}
                            className="p-1 text-brand-blue-600 hover:text-brand-blue-800"
                            title="View Enrolled Users"
                        >
                            <Users className="w-4 h-4" />
                        </Link>
                        </div>
                    </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>
            {coursesData && coursesData.pagination.total > 0 && (
                <Pagination
                    currentPage={filters.page}
                    totalPages={Math.ceil(coursesData.pagination.total / filters.limit)}
                    totalItems={coursesData.pagination.total}
                    itemsPerPage={filters.limit}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            )}
        </>
      )}
      </div>
    </div>
  );
}